import React from 'react';
import AntLocalization from '../../utils/AntLocalization';

// ─── Default event type config ──────────────────────────────────
const DEFAULT_EVENT_TYPES = {
    status_change: { icon: 'refresh', color: '#3B82F6', label: { vi: 'Đổi trạng thái', en: 'Status Change', fr: 'Changement statut', es: 'Cambio de estado', id: 'Ubah status', ja: 'ステータス変更', zh: '状态变更', ko: '상태 변경', hi: 'स्थिति बदली', ar: 'تغيير الحالة' } },
    comment: { icon: 'comment', color: '#10B981', label: { vi: 'Bình luận', en: 'Comment', fr: 'Commentaires', es: 'Comentarios', id: 'Komentar', ja: 'コメント', zh: '评论', ko: '댓글', hi: 'टिप्पणी', ar: 'التعليقات' } },
    file_upload: { icon: 'upload', color: '#F59E0B', label: { vi: 'Tải file', en: 'File Upload', fr: 'Téléversement', es: 'Carga archivo', id: 'Unggah file', ja: 'アップロード', zh: '上传文件', ko: '파일 업로드', hi: 'फ़ाइल अपलोड', ar: 'رفع ملف' } },
    approval: { icon: 'check', color: '#8B5CF6', label: { vi: 'Phê duyệt', en: 'Approval', fr: 'Approbation', es: 'Aprobación', id: 'Persetujuan', ja: '承認', zh: '审批', ko: '승인', hi: 'अनुमोदन', ar: 'الموافقة' } },
    rejection: { icon: 'close', color: '#EF4444', label: { vi: 'Từ chối', en: 'Rejection', fr: 'Rejet', es: 'Rechazo', id: 'Penolakan', ja: '却下', zh: '拒绝', ko: '반려', hi: 'अस्वीकृति', ar: 'الرفض' } },
    system: { icon: 'info', color: '#64748B', label: { vi: 'Hệ thống', en: 'System', fr: 'Système', es: 'Sistema', id: 'Sistem', ja: 'システム', zh: '系统', ko: '시스템', hi: 'सिस्टम', ar: 'النظام' } },
    assignment: { icon: 'user', color: '#EC4899', label: { vi: 'Giao việc', en: 'Assignment', fr: 'Affectation', es: 'Asignación', id: 'Penugasan', ja: '割当', zh: '指派', ko: '배정', hi: 'कार्य सौंपा', ar: 'الإسناد' } },
    created: { icon: 'add', color: '#06B6D4', label: { vi: 'Tạo mới', en: 'Created', fr: 'Créé', es: 'Creado', id: 'Dibuat', ja: '作成', zh: '创建', ko: '생성', hi: 'बनाया', ar: 'إنشاء' } },
    deleted: { icon: 'trash', color: '#EF4444', label: { vi: 'Xoá', en: 'Deleted', fr: 'Supprimé', es: 'Eliminado', id: 'Dihapus', ja: '削除', zh: '删除', ko: '삭제', hi: 'हटाया', ar: 'حذف' } }
};

// ─── Relative time ──────────────────────────────────────────────
function getRelativeTime(timestamp) {
    if (!timestamp) return '';
    var ts = typeof timestamp === 'number'
        ? (timestamp < 1e12 ? timestamp * 1000 : timestamp)
        : new Date(timestamp).getTime();
    var diff = Date.now() - ts;
    if (diff < 0) diff = 0;
    var mins = Math.floor(diff / 60000);
    var hours = Math.floor(diff / 3600000);
    var days = Math.floor(diff / 86400000);

    if (mins < 1) return AntLocalization.translateText({ vi: 'Vừa xong', en: 'Just now', fr: 'À l\'instant', es: 'Ahora', id: 'Baru saja', ja: 'たった今', zh: '刚刚', ko: '방금', hi: 'अभी', ar: 'الآن' });
    if (mins < 60) return mins + AntLocalization.translateText({ vi: ' phút trước', en: 'm ago', fr: ' min', es: ' min', id: ' mnt lalu', ja: '分前', zh: '分钟前', ko: '분 전', hi: ' मि. पहले', ar: ' د' });
    if (hours < 24) return hours + AntLocalization.translateText({ vi: ' giờ trước', en: 'h ago', fr: ' h', es: ' h', id: ' jam lalu', ja: '時間前', zh: '小时前', ko: '시간 전', hi: ' घं. पहले', ar: ' س' });
    if (days < 7) return days + AntLocalization.translateText({ vi: ' ngày trước', en: 'd ago', fr: ' j', es: ' d', id: ' hari lalu', ja: '日前', zh: '天前', ko: '일 전', hi: ' दिन पहले', ar: ' ي' });

    var d = new Date(ts);
    return d.toLocaleDateString('vi-VN');
}

function formatDateTime(timestamp) {
    if (!timestamp) return '';
    var ts = typeof timestamp === 'number'
        ? (timestamp < 1e12 ? timestamp * 1000 : timestamp)
        : new Date(timestamp).getTime();
    var d = new Date(ts);
    return d.toLocaleString('vi-VN');
}

// ─── Avatar ─────────────────────────────────────────────────────
function getInitials(name) {
    if (!name) return '?';
    var parts = name.split(' ').filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return name.substring(0, 2).toUpperCase();
}

function getAvatarColor(name) {
    if (!name) return '#9CA3AF';
    var hash = 0;
    for (var i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    var colors = ['#3B82F6', '#8B5CF6', '#EC4899', '#F59E0B', '#10B981', '#06B6D4', '#F97316', '#6366F1'];
    return colors[Math.abs(hash) % colors.length];
}

// ─── Component ──────────────────────────────────────────────────
export default function AntTimelineItem({ item, eventTypes, showTimestamp, compact }) {
    var allTypes = { ...DEFAULT_EVENT_TYPES, ...eventTypes };
    var evtType = allTypes[item.type] || allTypes.system;
    var userName = (item.user && item.user.name) || item.user_name || item.created_user_name || '';
    var avatarUrl = item.user && item.user.avatar;
    var contentText = typeof item.content === 'object' ? AntLocalization.translateText(item.content) : (item.content || item.description || '');

    return (
        <div className={'timeline-item' + (compact ? ' compact' : '')}>
            {/* Icon circle */}
            <div className="timeline-icon-wrap">
                <div className="timeline-icon" style={{ background: evtType.color }}>
                    <i className={'dx-icon-' + evtType.icon} />
                </div>
            </div>

            {/* Content card */}
            <div className="timeline-card">
                {/* Header: avatar + user + action + time */}
                <div className="timeline-header">
                    {!compact && (
                        avatarUrl
                            ? <img className="timeline-avatar" src={avatarUrl} alt="" />
                            : userName && (
                                <div className="timeline-avatar-text" style={{ background: getAvatarColor(userName) }}>
                                    {getInitials(userName)}
                                </div>
                            )
                    )}
                    {userName && <span className="timeline-user">{userName}</span>}
                    <span className="timeline-action" style={{ color: evtType.color }}>
                        {AntLocalization.translateText(evtType.label)}
                    </span>
                    <span className="timeline-time" title={formatDateTime(item.timestamp || item.created_at)}>
                        {showTimestamp === 'absolute'
                            ? formatDateTime(item.timestamp || item.created_at)
                            : getRelativeTime(item.timestamp || item.created_at)}
                    </span>
                </div>

                {/* Body */}
                <div className="timeline-body">
                    {item.type === 'status_change' && item.meta && (
                        <div className="timeline-status-change">
                            <span className="status-badge old">{item.meta.from}</span>
                            <i className="dx-icon-arrowright" />
                            <span className="status-badge new">{item.meta.to}</span>
                        </div>
                    )}

                    {item.type === 'comment' && contentText && (
                        <div className="timeline-comment-text">{contentText}</div>
                    )}

                    {item.type === 'file_upload' && item.meta && (
                        <a className="timeline-file-link" href={item.meta.url} target="_blank" rel="noopener noreferrer">
                            <i className="dx-icon-doc" /> {item.meta.filename || item.meta.name}
                        </a>
                    )}

                    {item.type !== 'status_change' && item.type !== 'comment' && item.type !== 'file_upload' && contentText && (
                        <div className="timeline-generic-text">{contentText}</div>
                    )}
                </div>
            </div>
        </div>
    );
}

export { DEFAULT_EVENT_TYPES, getRelativeTime, formatDateTime };
